import React, { useCallback, useEffect, useState } from 'react'
import {
  Button, Form, Input, Modal, Popconfirm, Space, Table, Tabs, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { get, post } from '../../lib/request'
import request from '../../lib/request'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'

const { Text } = Typography

interface BlacklistItem {
  id: number
  name: string
  phone: string
  reason?: string | null
  createdBy?: number | null
  createdAt?: string
  removedBy?: number | null
  removedAt?: string | null
  active: boolean
}

interface RosterItem {
  id: number
  name: string
  phone: string
  dept?: string | null
  isSalesRelated: boolean
  updatedBy?: number | null
  updatedAt?: string
}

const fmtTime = (t?: string | null) => (t ? dayjs(t).format('YYYY-MM-DD') : '—')

const del = <T,>(url: string): Promise<T> =>
  request.delete<{ code: number; data: T }>(url).then((res) => res.data.data)

const BlacklistPage: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  // HR 花名册 Tab 仅 admin/super_admin
  const canRoster = hasPermission(role, 'ROSTER_MANAGE')

  const [list, setList] = useState<BlacklistItem[]>([])
  const [loading, setLoading] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [addForm] = Form.useForm<{ name: string; phone: string; reason: string }>()
  const [addLoading, setAddLoading] = useState(false)

  const [roster, setRoster] = useState<RosterItem[]>([])
  const [rosterLoading, setRosterLoading] = useState(false)
  const [rosterQuery, setRosterQuery] = useState('')
  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState('')
  const [importLoading, setImportLoading] = useState(false)

  const fetchBlacklist = useCallback(() => {
    setLoading(true)
    get<BlacklistItem[] | { list: BlacklistItem[] }>('/admin/blacklist')
      .then((data) => setList(Array.isArray(data) ? data : data.list ?? []))
      .catch((err: unknown) => {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '黑名单加载失败（后端可能未启动）'
        message.warning(msg)
        setList([])
      })
      .finally(() => setLoading(false))
  }, [])

  const fetchRoster = useCallback(() => {
    if (!canRoster) return
    setRosterLoading(true)
    get<RosterItem[] | { list: RosterItem[] }>('/admin/hr-roster', {
      params: rosterQuery ? { keyword: rosterQuery } : {},
    })
      .then((data) => setRoster(Array.isArray(data) ? data : data.list ?? []))
      .catch((err: unknown) => {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '花名册加载失败（后端可能未启动）'
        message.warning(msg)
        setRoster([])
      })
      .finally(() => setRosterLoading(false))
  }, [canRoster, rosterQuery])

  useEffect(() => {
    fetchBlacklist()
  }, [fetchBlacklist])

  useEffect(() => {
    fetchRoster()
  }, [fetchRoster])

  const handleAdd = async () => {
    const values = await addForm.validateFields()
    setAddLoading(true)
    try {
      await post('/admin/blacklist', values)
      message.success('已加入黑名单')
      setAddOpen(false)
      addForm.resetFields()
      fetchBlacklist()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '新增失败'
      message.error(msg)
    } finally {
      setAddLoading(false)
    }
  }

  const handleRemove = async (record: BlacklistItem) => {
    try {
      await del(`/admin/blacklist/${record.id}`)
      message.success('已移出黑名单（软删除，留痕保留）')
      fetchBlacklist()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '移除失败'
      message.error(msg)
    }
  }

  const handleImport = async () => {
    // 支持 JSON 数组 或 行式 CSV：name,phone,dept[,isSalesRelated]
    let entries: Array<{ name: string; phone: string; dept?: string; isSalesRelated?: boolean }> = []
    const raw = importText.trim()
    if (!raw) {
      message.warning('请输入导入内容')
      return
    }
    try {
      if (raw.startsWith('[')) {
        entries = JSON.parse(raw)
      } else {
        entries = raw
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [name, phone, dept, sales] = line.split(/[,，\t]/).map((s) => s?.trim())
            return {
              name,
              phone,
              dept: dept || undefined,
              isSalesRelated: sales === '1' || sales === 'true' || sales === '是',
            }
          })
      }
    } catch {
      message.error('导入内容解析失败：请使用 JSON 数组或每行「姓名,手机号,部门,是否销售关联」')
      return
    }
    if (entries.some((e) => !e.name || !e.phone)) {
      message.error('存在缺少姓名或手机号的行')
      return
    }
    setImportLoading(true)
    try {
      await post('/admin/hr-roster', { entries })
      message.success(`批量导入成功（${entries.length} 条）`)
      setImportOpen(false)
      setImportText('')
      fetchRoster()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '导入失败'
      message.error(msg)
    } finally {
      setImportLoading(false)
    }
  }

  const blacklistColumns: ColumnsType<BlacklistItem> = [
    { title: '姓名', dataIndex: 'name', key: 'name' },
    { title: '手机号', dataIndex: 'phone', key: 'phone' },
    { title: '原因', dataIndex: 'reason', key: 'reason', render: (v?: string | null) => v ?? '—' },
    {
      title: '加入人 / 时间',
      key: 'created',
      render: (_: unknown, r) => `#${r.createdBy ?? '—'} · ${fmtTime(r.createdAt)}`,
    },
    {
      title: '状态',
      dataIndex: 'active',
      key: 'active',
      render: (v: boolean) => (v ? <Tag color="error">生效中</Tag> : <Tag color="success">已移出</Tag>),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      render: (_: unknown, r) =>
        r.active ? (
          <Popconfirm
            title="移出黑名单"
            description={`确认将「${r.name}」移出黑名单？操作将留痕保留。`}
            okText="确认移出"
            cancelText="取消"
            onConfirm={() => handleRemove(r)}
          >
            <Button size="small" style={{ minHeight: 32 }}>
              移出（留痕）
            </Button>
          </Popconfirm>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            移出人 #{r.removedBy ?? '—'} · {fmtTime(r.removedAt)}
          </Text>
        ),
    },
  ]

  const rosterColumns: ColumnsType<RosterItem> = [
    { title: '姓名', dataIndex: 'name', key: 'name' },
    { title: '手机号', dataIndex: 'phone', key: 'phone' },
    { title: '部门', dataIndex: 'dept', key: 'dept', render: (v?: string | null) => v ?? '—' },
    {
      title: '销售关联',
      dataIndex: 'isSalesRelated',
      key: 'isSalesRelated',
      render: (v: boolean) =>
        v ? <Tag color="error">是 · 禁止参与推荐</Tag> : <Tag color="success">否</Tag>,
    },
    { title: '更新人 / 时间', key: 'updated', render: (_: unknown, r) => `#${r.updatedBy ?? '—'} · ${fmtTime(r.updatedAt)}` },
  ]

  const tabItems = [
    {
      key: 'blacklist',
      label: '黑名单维护',
      children: (
        <>
          <Space style={{ marginBottom: 14 }}>
            <Button type="primary" style={{ minHeight: 44 }} onClick={() => setAddOpen(true)}>
              新增黑名单
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              运营维护，全部操作留痕；注册与发奖前系统自动比对。
            </Text>
          </Space>
          <Table<BlacklistItem>
            rowKey="id"
            loading={loading}
            columns={blacklistColumns}
            dataSource={list}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
          />
        </>
      ),
    },
  ]

  if (canRoster) {
    tabItems.push({
      key: 'roster',
      label: 'HR 花名册',
      children: (
        <>
          <Space style={{ marginBottom: 14 }} wrap>
            <Input.Search
              placeholder="按姓名 / 手机号查询"
              allowClear
              style={{ width: 260, minHeight: 44 }}
              onSearch={(v) => setRosterQuery(v)}
            />
            <Button type="primary" style={{ minHeight: 44 }} onClick={() => setImportOpen(true)}>
              批量导入
            </Button>
          </Space>
          <Table<RosterItem>
            rowKey="id"
            loading={rosterLoading}
            columns={rosterColumns}
            dataSource={roster}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
          />
        </>
      ),
    })
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 16 }}>
      <Tabs items={tabItems} />

      <Modal
        title="新增黑名单"
        open={addOpen}
        onOk={handleAdd}
        onCancel={() => setAddOpen(false)}
        okText="加入黑名单"
        cancelText="取消"
        confirmLoading={addLoading}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号"
            rules={[{ required: true, message: '请输入手机号' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="reason" label="原因" rules={[{ required: true, message: '请填写原因' }]}>
            <Input.TextArea rows={2} placeholder="例：招商部在职，禁止参与" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量导入 HR 花名册"
        open={importOpen}
        onOk={handleImport}
        onCancel={() => setImportOpen(false)}
        okText="导入"
        cancelText="取消"
        confirmLoading={importLoading}
        width={560}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          支持 JSON 数组（[{'{'}"name","phone","dept","isSalesRelated"{'}'}…]）或行式输入：每行
          「姓名,手机号,部门,是否销售关联（1/0）」。
        </Text>
        <Input.TextArea
          rows={8}
          style={{ marginTop: 8 }}
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          placeholder={'张三,13800000000,招商部,1\n李四,13900000000,行政部,0'}
        />
      </Modal>
    </div>
  )
}

export default BlacklistPage
